import React, { useEffect, useState } from 'react'
import Bugsnag from '@bugsnag/js'
import { HighlightData } from 'src/components/molecules/OCRHighlightOverlay/types'
import { RotationImage } from '../RotationImage'

export type LazyLoadImageProps = {
  sources: string[]
  alt: string
  placeholder: string
  highlights?: HighlightData[]
  showHighlights?: boolean
  showHighlightSelector?: boolean
  onChangeHighlightEnabled?: (enabled: boolean) => void
  onLoaded?: () => void
} & React.StyledProps

export const LazyLoadImage: React.StyledFC<LazyLoadImageProps> = ({
  className,
  sources,
  alt,
  placeholder,
  highlights,
  showHighlights,
  showHighlightSelector,
  onChangeHighlightEnabled,
  onLoaded,
}: LazyLoadImageProps) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false)

  const loadImage = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve()
      // NOTE: onerror が渡すのは Error ではなく Event のため、
      // そのまま reject すると Bugsnag.notify() が非Errorを受け取り
      // 「received a non-error」として原因の分からないレポートになる。
      image.onerror = () => reject(new Error(`failed to load image: ${src}`))
      image.src = src
    })
  }

  useEffect(() => {
    const promises: Promise<void>[] = []
    sources.forEach((src) => {
      promises.push(loadImage(src))
    })
    Promise.all(promises)
      .then(() => {
        setIsLoaded(true)
        if (isLoaded && onLoaded) {
          onLoaded()
        }
      })
      .catch((err) => {
        setIsLoaded(false)
        Bugsnag.notify(err)
      })
  }, [isLoaded, onLoaded, sources])

  return isLoaded ? (
    <div>
      {sources.map((src, index) => (
        <RotationImage
          key={src}
          src={src}
          alt={alt}
          pictureNumber={`${index + 1}/${sources.length}枚目`}
          imageIndex={index}
          highlights={highlights}
          showHighlights={showHighlights}
          showHighlightSelector={showHighlightSelector}
          onChangeHighlightEnabled={onChangeHighlightEnabled}
        />
      ))}
    </div>
  ) : (
    <img className={className} src={placeholder} alt={alt} draggable={false} />
  )
}
